// Tap sounds: what a spot in the scene answers a tap with (site/js/ui/spots.js names one per kind, player.tap plays
// it). Short one-shots, well under 2 s, pitched from the key sounding on the track's next beat and started on that
// beat, so a tap lands in the music whatever the style. They go to the timer cues' bus (player.js), which follows the
// louder slider, and are made like sounds.js's one-shots: every node in the registry, a little street echo.
//   nextBeat(start, bpm, at) · tapScale(key, plan) · tapNotes(sound, key, plan)   (pure: tests/spots.test.mjs)
//   playTap(ac, out, when, sound, midis, env, x) → the ac time it has ended by
import { clamp } from '../engine/core.js';
import { mtof, noise } from './dsp.js';
import { PENTA } from './harmony.js';
import { EVENTS } from './sounds.js';
import { SCALES as JAPANESE } from './styles/japanese-band.js';
import { RAGAS } from './styles/sitar-voice.js';
import { MODES as CHURCH } from './styles/medieval-band.js';

export const TAP_MAX = 3;             // taps sounding at once: one more plays nothing
// Voice levels on the cue bus, measured the way window.__runTimerCheck measures the cues (tools/browser/audio-check.html:
// each alone into the master at the default sliders): the loudest 400 ms of a tap sits 7 to 11 dB under the mix and
// peaks near −15 dBFS, where a timer cue sits 6 dB over the mix. `splash` is the gain given to sounds.js's one-shot.
// A sound's own `gain` multiplies its voice's: the chat's mention ding (site/js/live/chat.js MENTION_DING) is a glass
// rung twice as loud, since it has to be heard by someone who isn't looking: about 3 dB under the mix, peaking near
// −10 dBFS.
export const TAP_GAIN = { glass: 0.063, bell: 0.1, buzz: 0.16, mew: 0.25, flutter: 0.17, splash: 0.6 };
export const TAP_VOICES = Object.keys(TAP_GAIN);
const ECHO = 0.25;                    // post-level send to the street echo
const mod12 = n => ((n % 12) + 12) % 12;
const NO_KEY = { tonic: 0, mode: 'minor' };   // no track yet (or a playlist's): C minor, as snapToKey's default

/** The first beat at or after `at` on the grid of a track that started at `start` (ac seconds) at `bpm`. The grid
 *  runs on before the track's first bar and past its last, so a tap between two tracks lands on a beat too. */
export function nextBeat(start, bpm, at) {
  const beat = 60 / bpm;
  return start + Math.ceil((at - start) / beat - 1e-9) * beat;
}

/** The track's own scale (semitones over its tonic) where its style plays in one, else null: a maqam, a Japanese
 *  scale, a raga, a church mode. */
export const ownScale = plan => plan?.oud?.scale ?? JAPANESE[plan?.japanese?.scale] ?? RAGAS[plan?.sitar?.raga] ?? CHURCH[plan?.medieval?.mode] ?? null;

/** The semitones over the tonic a tap may sound in `key` on a track planned as `plan`: the key's pentatonic, without
 *  the notes the track's own scale leaves out. The tonic and the fifth are in every one of them. */
export function tapScale(key, plan = null) {
  const penta = PENTA[(key || NO_KEY).mode] || PENTA.minor, own = ownScale(plan);
  const iv = own ? penta.filter(i => own.includes(i)) : penta;
  return iv.length > 1 ? iv : [0, 7];
}

/** A sound's MIDI notes in `key`: sound.notes are semitones over the tonic at or above MIDI sound.from, each moved to
 *  the nearest note of tapScale (the lower of two as near), so a shape keeps its contour in every scale. */
export function tapNotes(sound, key, plan = null) {
  const k = key || NO_KEY, iv = tapScale(k, plan), base = sound.from + mod12(k.tonic - sound.from);
  return (sound.notes || []).map(n => {
    let best = 0, bd = Infinity;
    for (let oct = -24; oct <= 24; oct += 12) for (const i of iv) {
      const d = Math.abs(i + oct - n);
      if (d < bd) { bd = d; best = i + oct; }
    }
    return base + best;
  });
}

/* ---------------- voices ---------------- */
/** A voice's nodes: sources into `k.in`, panned, at `gain` into `out` and the echo; done(when, end) starts them,
 *  fades the voice out just before `end` and registers it. */
function voice(ac, out, env, gain, pan) {
  const nodes = [], src = [], N = n => (nodes.push(n), n);
  const vg = N(ac.createGain()), p = N(ac.createStereoPanner()), send = N(ac.createGain());
  vg.gain.value = gain; p.pan.value = pan; send.gain.value = ECHO;
  p.connect(vg); vg.connect(out); vg.connect(send); send.connect(env.echo);
  return {
    in: p,
    G: (v = 0) => { const g = N(ac.createGain()); g.gain.value = v; return g; },
    F: (type, f, Q) => { const b = N(ac.createBiquadFilter()); b.type = type; b.frequency.value = f; b.Q.value = Q; return b; },
    O: (type, f) => { const o = N(ac.createOscillator()); o.type = type; o.frequency.value = f; src.push(o); return o; },
    B: buf => { const s = N(ac.createBufferSource()); s.buffer = buf; s.loop = true; src.push(s); return s; },
    done(when, end) {
      vg.gain.setValueAtTime(gain, end - 0.04); vg.gain.setTargetAtTime(0, end - 0.04, 0.01);
      for (const s of src) { s.start(when, s.buffer ? env.r() * s.buffer.duration * 0.5 : 0); s.stop(end); }
      env.registry.add({ src, all: nodes, start: when, end, tag: `${env.tag}:ev`, out: vg });
      return end;
    },
  };
}
const hit = (param, peak, t, atk, tau) => { param.setValueAtTime(0, t); param.linearRampToValueAtTime(peak, t + atk); param.setTargetAtTime(0, t + atk, tau); };
const gate = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };

// Struck things, as partials [ratio, level, share of the decay]: a glass (a pane, a lamp, a lit landmark) and
// sounds.js's bell, rung short.
const PARTIALS = {
  glass: [[1, 1, 1], [2.76, 0.3, 0.45], [5.4, 0.12, 0.25]],
  bell: [[0.5, 0.35, 1], [1, 1, 0.8], [1.19, 0.5, 0.6], [1.5, 0.3, 0.5], [2, 0.45, 0.4], [2.5, 0.2, 0.25]],
};
/** each note `gap` s after the one before, ringing off with τ `decay` */
function struck(k, when, midis, partials, { gap = 0.09, decay = 0.22 }) {
  midis.forEach((m, i) => {
    const t = when + i * gap, f = mtof(m);
    for (const [ratio, level, share] of partials) {
      const o = k.O('sine', f * ratio), g = k.G(0);
      hit(g.gain, level, t, 0.004, decay * share);
      o.connect(g); g.connect(k.in);
    }
  });
  return when + Math.max(0, midis.length - 1) * gap + decay * 5;
}

const VOICES = {
  glass: (k, when, midis, sound) => struck(k, when, midis, PARTIALS.glass, sound),
  bell: (k, when, midis, sound) => struck(k, when, midis, PARTIALS.bell, sound),
  /** a neon tube catching: two stutters, then it hums and fades */
  buzz(k, when, midis) {
    const f = mtof(midis[0]), a = k.O('sawtooth', f), b = k.O('sawtooth', f * 1.01), lp = k.F('lowpass', 1400, 2), g = k.G(0);
    a.connect(lp); b.connect(lp); lp.connect(g); g.connect(k.in);
    gate(g.gain, 0.5, when, 0.003, 0.04, 0.008);
    gate(g.gain, 0.5, when + 0.09, 0.003, 0.05, 0.008);
    gate(g.gain, 0.5, when + 0.2, 0.01, 0.1, 0.16);
    return when + 1.1;
  },
  /** a cat's meow, "mi-a-ow": the voice scoops up from the first note to the second and falls back under it, through
   *  a mouth that opens and closes. Two formants say the vowels (the first rises and falls, the second falls all the
   *  way: one filter sweeping up and down says "wah"), a hum under them is the closed-mouth "m", and a little breath
   *  and an unsteady pitch keep it an animal's. */
  mew(k, when, midis, sound, ac) {
    const lo = mtof(midis[0]), hi = mtof(midis[1] ?? midis[0]);
    const o = k.O('sawtooth', lo), tilt = k.F('lowpass', 3800, 0.5), amp = k.G(0);
    const f1 = k.F('bandpass', 450, 4), f2 = k.F('bandpass', 2600, 6), g2 = k.G(0.55), hum = k.F('lowpass', 900, 0.5), gh = k.G(0.12);
    const n = k.B(noise(ac, 'white')), slow = k.F('lowpass', 30, 0.5), jit = k.G(lo * 0.3), breath = k.G(0.3);
    o.frequency.setValueAtTime(lo * 0.94, when); o.frequency.exponentialRampToValueAtTime(hi, when + 0.16);
    o.frequency.setValueAtTime(hi, when + 0.24); o.frequency.exponentialRampToValueAtTime(lo, when + 0.46);
    o.frequency.exponentialRampToValueAtTime(lo * 0.94, when + 0.62);
    f1.frequency.setValueAtTime(450, when + 0.05); f1.frequency.linearRampToValueAtTime(800, when + 0.16);
    f1.frequency.linearRampToValueAtTime(1350, when + 0.3); f1.frequency.linearRampToValueAtTime(600, when + 0.62);
    f2.frequency.setValueAtTime(2600, when + 0.05); f2.frequency.linearRampToValueAtTime(3000, when + 0.16);
    f2.frequency.linearRampToValueAtTime(2300, when + 0.3); f2.frequency.linearRampToValueAtTime(1200, when + 0.62);
    amp.gain.setValueAtTime(0, when); amp.gain.linearRampToValueAtTime(0.3, when + 0.05);
    amp.gain.linearRampToValueAtTime(1, when + 0.2); amp.gain.setValueAtTime(1, when + 0.34); amp.gain.setTargetAtTime(0, when + 0.34, 0.085);
    n.connect(slow); slow.connect(jit); jit.connect(o.frequency);
    n.connect(breath); breath.connect(f2);
    o.connect(tilt); tilt.connect(f1); tilt.connect(f2); tilt.connect(hum);
    f1.connect(amp); f2.connect(g2); g2.connect(amp); hum.connect(gh); gh.connect(amp); amp.connect(k.in);
    return when + 0.8;
  },
  /** pigeons taking off: wingbeats that thin out, over a two-note coo */
  flutter(k, when, midis, sound, ac) {
    const n = k.B(noise(ac, 'white')), bp = k.F('bandpass', 1500, 0.9), g = k.G(0);
    n.connect(bp); bp.connect(g); g.connect(k.in);
    for (let i = 0; i < 7; i++) gate(g.gain, 1 - i * 0.11, when + i * 0.07 + i * i * 0.004, 0.004, 0.022, 0.012);
    midis.slice(0, 2).forEach((m, i) => {
      const o = k.O('sine', mtof(m)), cg = k.G(0), t = when + i * 0.17;
      cg.gain.setValueAtTime(0, t); cg.gain.linearRampToValueAtTime(0.5, t + 0.03); cg.gain.setTargetAtTime(0, t + 0.09, 0.04);
      o.connect(cg); cg.connect(k.in);
    });
    return when + 0.95;
  },
};

/**
 * Plays `sound` ({ voice, from, notes, gap, decay, gain }, a spots.js KINDS entry's) at ac time `when` into `out`, its
 * notes `midis` (tapNotes), panned to scene column `x`. env: { registry, tag, echo, r }. Returns the ac time it has ended
 * by. 'splash' is sounds.js's own one-shot, as the scenes play it, with the notes as drops on top.
 */
export function playTap(ac, out, when, sound, midis, env, x = null) {
  const pan = x == null ? 0 : clamp(x / 480 * 2 - 1, -1, 1) * 0.6, gain = TAP_GAIN[sound.voice] * (sound.gain ?? 1);
  if (sound.voice === 'splash') {
    EVENTS.splash(ac, out, when, { type: 'splash', pan, gain }, env);
    const k = voice(ac, out, env, TAP_GAIN.glass, pan);
    return Math.max(when + 1.2, k.done(when, struck(k, when + 0.05, midis, PARTIALS.glass, { gap: 0.11, decay: 0.07 })));
  }
  const k = voice(ac, out, env, gain, pan);
  return k.done(when, VOICES[sound.voice](k, when, midis, sound, ac));
}
